import React, { useState, useCallback, useEffect } from 'react';
import { not, without } from 'ramda';
import PropTypes from 'prop-types';

import Close from 'assets/svg-icons/SmallClose';
import {
  FILE_ID,
  FILE_URL,
  MIMETYPE,
  ORIGINAL_NAME,
  NAME,
  TAGS,
  FILE_URL_EXPIRES,
  BUCKET,
  useInfoItemsActions,
  useAlerts,
} from 'store/infoItems';
import Warn from 'components/WarnDeleting';
import ImagesReview from 'components/ImagesReview';

import { Container, HeadWrap, MenuBtn, ButtonWrap, Button, Preview } from './styles';

const FileForm = ({ fileData, setFiles, readMode, attachments }) => {
  const [isShowWarn, setIsShowWarn] = useState(false);
  const [imageUrl, setImageUrl] = useState();
  const { deleteFile } = useInfoItemsActions();
  const { action, loading, success, resetAlerts } = useAlerts(deleteFile);

  const updateFile = useCallback(
    (arg) =>
      setFiles(($) =>
        $.map((item) =>
          item[FILE_ID] === fileData[FILE_ID] ? { ...item, ...(typeof arg === 'function' ? arg(item) : arg) } : item
        )
      ),
    [fileData, setFiles]
  );
  const handleTags = useCallback(
    (e) => {
      if (readMode) return;
      const { id } = e.currentTarget?.dataset || {};

      updateFile(($) => ({ ...$, [TAGS]: $[TAGS].includes(id) ? without([id], $[TAGS]) : [...$[TAGS], id] }));
    },
    [readMode, updateFile]
  );
  const handleShowWarn = useCallback(() => setIsShowWarn(not), []);
  const handleDelete = useCallback(() => {
    if (fileData[FILE_ID] && fileData[BUCKET]) action(fileData);
  }, [action, fileData]);
  const handleClosePreview = useCallback(() => setImageUrl(), []);

  useEffect(() => {
    if (success) {
      setFiles(($) => $.filter((item) => item[FILE_ID] !== success));
      resetAlerts();
    }
  }, [resetAlerts, setFiles, success]);

  return (
    <Container>
      {readMode || (
        <HeadWrap>
          <MenuBtn type="button" title="Löschen Sie" onClick={handleShowWarn}>
            <Close />
          </MenuBtn>
        </HeadWrap>
      )}
      <Preview data={fileData} setImageUrl={setImageUrl} />
      <ButtonWrap>
        <Button
          type="button"
          data-id="Einverständiserklärung"
          color={fileData[TAGS].includes('Einverständiserklärung') ? 'success' : 'default'}
          onClick={handleTags}
          disabled={readMode}
        >
          Einverständiserklärung
        </Button>
        <Button
          type="button"
          data-id="Externer Bericht"
          color={fileData[TAGS].includes('Externer Bericht') ? 'success' : 'default'}
          onClick={handleTags}
          disabled={readMode}
        >
          Externer Bericht
        </Button>
      </ButtonWrap>
      {isShowWarn && (
        <Warn
          message="Wollen Sie die angehängte Datei wirklich löschen?"
          showActon={handleShowWarn}
          deleteAction={handleDelete}
          loading={loading}
        />
      )}
      {imageUrl && <ImagesReview images={attachments} currentImage={imageUrl} onClose={handleClosePreview} />}
    </Container>
  );
};

FileForm.defaultProps = {
  readMode: false,
  attachments: [],
};

const fileDataPropType = PropTypes.shape({
  [FILE_ID]: PropTypes.string.isRequired,
  [FILE_URL]: PropTypes.string,
  [FILE_URL_EXPIRES]: PropTypes.string,
  [MIMETYPE]: PropTypes.string.isRequired,
  [ORIGINAL_NAME]: PropTypes.string.isRequired,
  [NAME]: PropTypes.string,
  [TAGS]: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
  [BUCKET]: PropTypes.string.isRequired,
});

FileForm.propTypes = {
  fileData: fileDataPropType.isRequired,
  setFiles: PropTypes.func.isRequired,
  readMode: PropTypes.bool,
  attachments: PropTypes.arrayOf(fileDataPropType.isRequired),
};

export default FileForm;
